import dayjs from 'dayjs';
import type { SetStateAction } from 'jotai';
import { cloneDeep } from 'lodash';
import type { Dispatch } from 'react';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';

import {
  YEAR_MONTH_DAY_FORMAT,
  YEAR_MONTH_DAY_TIME_FORMAT
} from '@/constants/date.constants.ts';

import {
  loadMicrosoftDataProvidersFormValues,
  loadS3DataProvidersFormValues
} from '../DataProvider/DataProvider.service';
import SOURCE_TYPE, {
  CROWDSTRIKE_CSPM_CATEGORIES
} from '../SourceCreate.constants';
import type {
  AlertDeviceField,
  ConnectionInputSchema,
  DynamicFieldData,
  LimaDevicesDTO,
  SourceCreateDynamicFormModel,
  SourceDetailResponse
} from '../SourceCreate.types';
import { formatIfValidDate } from '../SourceCreate.utils';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Microsoft source types that support certificate-based authentication.
 */
export const MICROSOFT_SOURCE_TYPES = [
  SOURCE_TYPE.MICROSOFT_SENTINEL,
  SOURCE_TYPE.MICROSOFT
] as const;

// ============================================================================
// HELPER FUNCTIONS - Default Values
// ============================================================================

/**
 * Loads default form values from the selected source details.
 *
 * This populates core fields (source type, device, IP, etc.) and optional
 * vulnerability/custom object configuration fields if present.
 *
 * @param defaultValuesModel - Initial model to build upon
 * @param selectedSourceDetails - Source details from the API
 * @param orgLabel - Organization label for service name extraction
 * @returns Updated default values model
 */
export const loadDefaultValues = (
  defaultValuesModel: SourceCreateDynamicFormModel,
  selectedSourceDetails: SourceDetailResponse,
  orgLabel: string
): SourceCreateDynamicFormModel => {
  // Extract service name by removing org prefix if present
  const extractServiceName = (sourceName: string): string => {
    return sourceName.startsWith(`${orgLabel}-`)
      ? sourceName.substring(orgLabel.length + 1)
      : sourceName;
  };

  // Build core default values with conditional fields using spread operator
  let defaultValues: SourceCreateDynamicFormModel = {
    ...defaultValuesModel,
    source_type_id: selectedSourceDetails.source_type_id,
    source_type_name: selectedSourceDetails.name,
    device_uid: selectedSourceDetails?.device_uid,
    ip_address: selectedSourceDetails.ip_address,
    source_name: selectedSourceDetails.source_name,
    site_name: selectedSourceDetails?.site_name || '',
    service_detail_name: extractServiceName(selectedSourceDetails.source_name),
    active: selectedSourceDetails?.active || false,
    // Vulnerability scanning configuration
    ...(selectedSourceDetails?.fetch_device_vuln !== undefined && {
      fetch_device_vuln: selectedSourceDetails.fetch_device_vuln
    }),
    ...(selectedSourceDetails?.vuln_scan_time_interval && {
      vuln_scan_time_interval: selectedSourceDetails.vuln_scan_time_interval
    }),
    ...(selectedSourceDetails?.vuln_api_count && {
      vuln_api_count: selectedSourceDetails.vuln_api_count
    }),
    ...(selectedSourceDetails?.vuln_event_count && {
      vuln_event_count: selectedSourceDetails.vuln_event_count
    }),
    // Custom object configuration
    ...(selectedSourceDetails?.fetch_custom_object !== undefined && {
      fetch_custom_object: selectedSourceDetails.fetch_custom_object
    }),
    ...(selectedSourceDetails?.custom_object_time_interval && {
      custom_object_time_interval:
        selectedSourceDetails.custom_object_time_interval
    }),
    ...(selectedSourceDetails?.custom_object_ingest_from && {
      custom_object_ingest_from: selectedSourceDetails.custom_object_ingest_from
    }),
    ...(selectedSourceDetails?.custom_objects && {
      custom_objects: selectedSourceDetails.custom_objects
    })
  };

  // Add file upload name if present
  if (selectedSourceDetails?.file_name) {
    defaultValues = {
      ...defaultValues,
      ['file_upload' as string]: selectedSourceDetails.file_name
    };
  }

  // Add edit status if present
  if (selectedSourceDetails?.edit_status) {
    defaultValues = {
      ...defaultValues,
      ['edit_status' as string]: selectedSourceDetails.edit_status
    };
  }

  // Handle CrowdStrike category filtering (remove CSPM categories)
  if (
    selectedSourceDetails?.category &&
    Number(selectedSourceDetails?.source_type_id) === SOURCE_TYPE.CROWD_STRIKE
  ) {
    defaultValues = {
      ...defaultValues,
      ['category' as string]:
        selectedSourceDetails.category.filter(
          (categoryName: string) =>
            !CROWDSTRIKE_CSPM_CATEGORIES.includes(
              categoryName as (typeof CROWDSTRIKE_CSPM_CATEGORIES)[number]
            )
        ) || []
    };
  } else if (selectedSourceDetails?.category) {
    defaultValues = {
      ...defaultValues,
      ['category' as string]: selectedSourceDetails.category
    };
  }

  // Add region if present
  if (selectedSourceDetails?.region) {
    defaultValues = {
      ...defaultValues,
      ['region' as string]: selectedSourceDetails.region
    };
  }

  // Add organization ID if present
  if (selectedSourceDetails?.organization_id) {
    defaultValues = {
      ...defaultValues,
      ['org_id' as string]: selectedSourceDetails.organization_id
    };
  }

  return defaultValues;
};

// ============================================================================
// HELPER FUNCTIONS - Authentication & Certificates
// ============================================================================

/**
 * Determines and sets the authentication mode based on certificate flow flags.
 *
 * Microsoft sources support two authentication methods:
 * 1. Client Secret - Traditional secret-based authentication
 * 2. Certificate - Certificate-based authentication (more secure)
 *
 * Certificate flow can use:
 * - New Certificate: Generate a new certificate for this source
 * - Existing Certificate: Reuse a previously uploaded certificate
 *
 * @param selectedSourceDetails - Source details containing certificate flow flags
 * @param setAuthenticationMode - Function to set the authentication mode
 * @param setNewCertificate - Function to set whether using a new certificate
 */
export const handleAuthenticationMode = (
  selectedSourceDetails: SourceDetailResponse,
  setAuthenticationMode: (mode: string) => void,
  setNewCertificate: (newCert: boolean) => void
): void => {
  const isCertFlow = selectedSourceDetails.is_certificate_flow;
  const isNewCert = selectedSourceDetails.is_new_certificate_flow;

  if (isCertFlow) {
    setAuthenticationMode('certificate');
    setNewCertificate(isNewCert ?? false);
  } else {
    setAuthenticationMode('client_secret');
    setNewCertificate(false);
  }
};

/**
 * Checks if the source type supports certificate-based authentication.
 *
 * Only Microsoft services (O365, Sentinel, Defender, Entra ID, and unified
 * Microsoft source type) support certificate authentication.
 *
 * @param sourceTypeId - The source type identifier
 * @param authMode - The authentication mode (certificate or client_secret)
 * @returns True if this is a Microsoft source with valid auth mode
 */
export const isCertificateBasedService = (
  sourceTypeId: number,
  authMode: string
): boolean => {
  const isMicrosoftSource = MICROSOFT_SOURCE_TYPES.includes(sourceTypeId);
  const isValidAuthMode =
    authMode === 'certificate' || authMode === 'client_secret';

  return isMicrosoftSource && isValidAuthMode;
};

/**
 * Configures certificate-related fields in the connection input schema.
 *
 * For certificate-based authentication, we need to add existing certificate
 * fields (thumbprint and expiration date) to the form schema so users can
 * view and manage their certificates.
 *
 * This function:
 * 1. Checks if the source uses certificate authentication
 * 2. Finds existing certificate fields from the schema
 * 3. Adds thumbprint and expiration fields if not already present
 * 4. Formats the expiration date for display
 *
 * @param selectedSourceDetails - Source details containing certificate flow flag
 * @param schema - Current connection input schema
 * @returns Updated schema with certificate fields (or original if not needed)
 */
export const configureCertificateFields = (
  selectedSourceDetails: SourceDetailResponse,
  schema: ConnectionInputSchema
): ConnectionInputSchema => {
  // Early return if not using certificate authentication
  if (!selectedSourceDetails?.is_certificate_flow) {
    return schema;
  }

  const existingInputs = schema.source_inputs || [];

  // Extract relevant certificate fields (thumbprint and expiration)
  const certificateFieldsToAdd =
    schema.existing_certificate_inputs
      ?.filter(
        (field) =>
          field.name === 'existing_thumbprint_id' || field.name === 'expires'
      )
      .filter((field) => !existingInputs.some((x) => x.name === field.name))
      .map((field) => {
        // Create a new field object (immutable update)
        const newField = { ...field };

        // Format the expiration date for display
        if (newField.name === 'expires' && selectedSourceDetails?.expires) {
          newField.value = dayjs
            .utc(selectedSourceDetails.expires)
            .local()
            .format(YEAR_MONTH_DAY_FORMAT);
        }

        return newField;
      }) || [];

  // Return original schema if no fields need to be added (prevents unnecessary re-renders)
  if (certificateFieldsToAdd.length === 0) {
    return schema;
  }

  // Return new schema with certificate fields added
  return {
    ...schema,
    source_inputs: [...existingInputs, ...certificateFieldsToAdd]
  };
};

// ============================================================================
// HELPER FUNCTIONS - Dynamic Field Configuration
// ============================================================================

/**
 * Extracts and configures client secret field from source details.
 *
 * The client secret value can be stored as either a string or an array
 * (depending on source type). This function normalizes it to a string.
 *
 * @param selectedSourceDetails - Source details containing client secret inputs
 * @param defaultValuesModel - Default values model to build upon
 * @returns Updated default values with client secret
 */
export const configureClientSecretFields = (
  selectedSourceDetails: SourceDetailResponse,
  defaultValuesModel: SourceCreateDynamicFormModel
): SourceCreateDynamicFormModel => {
  const clientSecretField = selectedSourceDetails.client_secret_inputs?.find(
    (field: DynamicFieldData) => field.name === 'client_secret'
  );

  if (!clientSecretField) {
    return defaultValuesModel;
  }

  // Normalize client secret value (handle both array and string)
  const clientSecretValue = Array.isArray(clientSecretField.value)
    ? clientSecretField.value[0] || ''
    : clientSecretField.value || '';

  return {
    ...defaultValuesModel,
    client_secret: clientSecretValue
  };
};

/**
 * Configures subscription type for Microsoft sources.
 *
 * Microsoft Defender and related sources use subscription types to determine
 * which data streams to ingest. This function extracts and sets the
 * subscription type from the source details.
 *
 * @param selectedSourceDetails - Source details containing subscription type
 * @param setSelectedSubscriptionType - Function to set the subscription type
 */
export const configureSubscriptionFields = (
  selectedSourceDetails: SourceDetailResponse,
  setSelectedSubscriptionType: (type: string) => void
): void => {
  const subscriptionTypeField = selectedSourceDetails.source_inputs?.find(
    (field: DynamicFieldData) => field.name === 'subscription_type'
  );

  if (!subscriptionTypeField?.value) {
    return;
  }

  // Normalize subscription type (handle both string and array)
  const subscriptionType =
    typeof subscriptionTypeField.value === 'string'
      ? subscriptionTypeField.value
      : Array.isArray(subscriptionTypeField.value) &&
          subscriptionTypeField.value.length > 0
        ? subscriptionTypeField.value[0]
        : '';

  if (subscriptionType) {
    setSelectedSubscriptionType(subscriptionType);
  }
};

/**
 * Configures dynamic form fields from source input data.
 *
 * This function processes all dynamic fields from the source details and adds
 * them to the default values model. It handles special cases:
 * - Date fields (expires, backup_expires, ingest_from) - formatted appropriately
 * - Thumbprint ID - mapped to existing_thumbprint_id
 * - Empty/invalid values - filtered out
 *
 * @param selectedSourceDetails - Source details containing dynamic field data
 * @param defaultValuesModel - Default values model to build upon
 * @returns Updated default values with all dynamic fields
 */
export const configureDynamicFields = (
  selectedSourceDetails: SourceDetailResponse,
  defaultValuesModel: SourceCreateDynamicFormModel
): SourceCreateDynamicFormModel => {
  let defaultValues = { ...defaultValuesModel };

  // Process each dynamic field from source inputs
  selectedSourceDetails?.source_inputs?.forEach((field: DynamicFieldData) => {
    // Handle date fields with special formatting
    if (
      field.name === 'expires' ||
      field.name === 'backup_expires' ||
      field.name === 'ingest_from'
    ) {
      if (field.value) {
        const valueToFormat = Array.isArray(field.value)
          ? field.value[0]
          : field.value;
        defaultValues = formatIfValidDate(
          valueToFormat,
          field.name,
          YEAR_MONTH_DAY_FORMAT,
          YEAR_MONTH_DAY_TIME_FORMAT,
          defaultValues
        );
      }
    }
    // Map thumbprint_id to existing_thumbprint_id
    else if (field.name === 'thumbprint_id') {
      defaultValues = {
        ...defaultValues,
        ['existing_thumbprint_id' as string]: field.value || null
      };
    }
    // Add non-empty string or valid number fields
    else if (
      (typeof field.value === 'string' && field.value.trim().length > 0) ||
      (typeof field.value === 'number' && !Number.isNaN(field.value))
    ) {
      defaultValues = { ...defaultValues, [field.name]: field.value };
    }
  });

  // Handle top-level expiration fields (for certificate-based sources)
  if (selectedSourceDetails?.expires) {
    defaultValues = formatIfValidDate(
      selectedSourceDetails.expires,
      'expires',
      YEAR_MONTH_DAY_FORMAT,
      YEAR_MONTH_DAY_FORMAT,
      defaultValues
    );
  }

  if (selectedSourceDetails?.backup_expires) {
    defaultValues = formatIfValidDate(
      selectedSourceDetails.backup_expires,
      'backup_expires',
      YEAR_MONTH_DAY_FORMAT,
      YEAR_MONTH_DAY_FORMAT,
      defaultValues
    );
  }

  return defaultValues;
};

/**
 * Configures device details by matching device_uid to Lima device records.
 *
 * Lima is the internal device management system. This function looks up the
 * device by UID and populates the device name in the form.
 *
 * @param selectedSourceDetails - Source details containing device UID
 * @param limaDevices - Available Lima device records
 * @param setValue - React Hook Form setValue function
 */
export const configureDeviceDetails = (
  selectedSourceDetails: SourceDetailResponse,
  limaDevices: LimaDevicesDTO[],
  setValue: UseFormSetValue<FieldValues>
): void => {
  const device = limaDevices.find(
    (d) => d.device_uid === selectedSourceDetails?.device_uid
  );

  if (device) {
    setValue('device_uid', device.device_uid);
    setValue('device_name', device.host_name);
  }
};

// ============================================================================
// POPULATION ORCHESTRATION HELPERS
// ============================================================================

/**
 * Step 1: Configures certificate fields in schema if source uses certificate auth.
 *
 * For Microsoft sources with certificate authentication, we need to add
 * certificate-related fields (thumbprint, expiration) to the form schema.
 */
export const populateCertificateConfiguration = (
  selectedSourceDetails: SourceDetailResponse,
  connectionInputSchema: ConnectionInputSchema,
  setConnectionInputSchema: Dispatch<SetStateAction<ConnectionInputSchema>>
): void => {
  const needsCertificateFlow = isCertificateBasedService(
    selectedSourceDetails.source_type_id,
    'certificate'
  );

  if (needsCertificateFlow) {
    const schemaCopy = cloneDeep(connectionInputSchema);
    const updatedSchema = configureCertificateFields(
      selectedSourceDetails,
      schemaCopy
    );
    if (updatedSchema !== schemaCopy) {
      setConnectionInputSchema(updatedSchema);
    }
  }
};

/**
 * Step 2: Builds core form default values from source details.
 *
 * Loads essential fields like source type, device, IP address, site name,
 * and optional vulnerability/custom object configuration.
 */
export const populateCoreFormFields = (
  selectedSourceDetails: SourceDetailResponse,
  orgLabel: string
): SourceCreateDynamicFormModel => {
  const defaultValues = {} as SourceCreateDynamicFormModel;
  return loadDefaultValues(defaultValues, selectedSourceDetails, orgLabel);
};

/**
 * Step 3-4: Configures categories, regions, and cloud providers.
 *
 * Handles source-specific category filtering (e.g., CrowdStrike CSPM categories)
 * and sets up region/cloud provider selections.
 */
export const populateCategoriesRegionsProviders = (
  selectedSourceDetails: SourceDetailResponse,
  setCategories: (val: string[]) => void,
  setRegion: (val: string[]) => void,
  setCloudProvider: (val: string[]) => void,
  setValue: UseFormSetValue<FieldValues>
): void => {
  // Configure categories (with CrowdStrike filtering)
  const categories = selectedSourceDetails?.category || [];
  const isCrowdStrike =
    Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.CROWD_STRIKE;

  const filteredCategories = isCrowdStrike
    ? categories.filter(
        (category: string) =>
          !CROWDSTRIKE_CSPM_CATEGORIES.includes(
            category as (typeof CROWDSTRIKE_CSPM_CATEGORIES)[number]
          )
      )
    : categories;

  setCategories(filteredCategories);
  setValue('category', filteredCategories);

  // Configure regions
  setRegion(selectedSourceDetails?.region || []);
  setValue('region', selectedSourceDetails?.region || []);

  // Configure cloud providers
  setCloudProvider(selectedSourceDetails?.cloud_provider || []);
  setValue('cloud_provider', selectedSourceDetails?.cloud_provider || []);
};

/**
 * Step 5-6: Configures time intervals, vulnerability fields, and alert fields.
 *
 * Sets up polling intervals for different data types and configures
 * vulnerability scanning and alert field selections.
 */
export const populateTimeAndVulnerabilityFields = (
  selectedSourceDetails: SourceDetailResponse,
  defaultValuesModel: SourceCreateDynamicFormModel,
  setSelectedTimeInterval: (val: string) => void,
  setSelectedSubTimeInterval: (val: string) => void,
  setVulnFields: (val: string[]) => void,
  setDeviceAlertFields: (val: AlertDeviceField[]) => void
): SourceCreateDynamicFormModel => {
  let defaultValues = { ...defaultValuesModel };

  // Configure time intervals
  if (selectedSourceDetails?.time_interval) {
    defaultValues = {
      ...defaultValues,
      ['time_interval' as string]: selectedSourceDetails.time_interval
    };
    setSelectedTimeInterval(selectedSourceDetails.time_interval);
  }

  if (selectedSourceDetails?.sub_time_interval) {
    defaultValues = {
      ...defaultValues,
      ['sub_time_interval' as string]: selectedSourceDetails.sub_time_interval
    };
    setSelectedSubTimeInterval(selectedSourceDetails.sub_time_interval);
  }

  // Configure vulnerability fields
  if (selectedSourceDetails?.vuln_fields) {
    defaultValues = {
      ...defaultValues,
      ['vuln_fields' as string]: selectedSourceDetails.vuln_fields
    };
    setVulnFields(selectedSourceDetails.vuln_fields);
  }

  // Combine alert_device_fields and alert_fields into a single array
  if (
    selectedSourceDetails?.alert_device_fields ||
    selectedSourceDetails?.alert_fields
  ) {
    const deviceAlertFields: AlertDeviceField[] = [
      ...(selectedSourceDetails.alert_device_fields?.map((field: string) => ({
        key: field,
        category: 'alert_device_fields' as const
      })) || []),
      ...(selectedSourceDetails.alert_fields?.map((field: string) => ({
        key: field,
        category: 'alert_fields' as const
      })) || [])
    ];

    defaultValues = {
      ...defaultValues,
      ['device_alert_fields' as string]: deviceAlertFields
    };
    setDeviceAlertFields(deviceAlertFields);
  }

  return defaultValues;
};

/**
 * Step 7: Configures authentication mode and client secret fields.
 *
 * Sets up authentication method (certificate vs client secret) for
 * Microsoft sources and loads client secret values if applicable.
 */
export const populateAuthenticationFields = (
  selectedSourceDetails: SourceDetailResponse,
  defaultValuesModel: SourceCreateDynamicFormModel,
  setAuthenticationMode: (mode: string) => void,
  setNewCertificate: (val: boolean) => void
): SourceCreateDynamicFormModel => {
  let defaultValues = { ...defaultValuesModel };

  // Configure authentication mode
  handleAuthenticationMode(
    selectedSourceDetails,
    setAuthenticationMode,
    setNewCertificate
  );

  // For Microsoft sources using client secrets
  if (
    isCertificateBasedService(
      selectedSourceDetails.source_type_id,
      'client_secret'
    )
  ) {
    defaultValues = configureClientSecretFields(
      selectedSourceDetails,
      defaultValues
    );
  }

  return defaultValues;
};

/**
 * Step 8: Configures subscription-specific settings for Microsoft sources.
 *
 * Microsoft Defender, O365, Entra ID, and unified Microsoft sources
 * use subscription types to determine which data streams to ingest.
 */
export const populateSubscriptionConfiguration = (
  selectedSourceDetails: SourceDetailResponse,
  setSelectedSubscriptionType: (val: string) => void
): void => {
  const microsoftSubscriptionSources = [
    SOURCE_TYPE.MICROSOFT_DEFENDER,
    SOURCE_TYPE.O365,
    SOURCE_TYPE.MICROSOFT_ENTRA_ID,
    SOURCE_TYPE.MICROSOFT
  ];

  if (
    microsoftSubscriptionSources.includes(
      Number(selectedSourceDetails.source_type_id)
    )
  ) {
    configureSubscriptionFields(
      selectedSourceDetails,
      setSelectedSubscriptionType
    );
  }
};

/**
 * Step 9: Configures source-type-specific settings.
 *
 * Handles special configuration for:
 * - Mimecast: API version selection (V1 vs V2)
 * - Cisco Secure Endpoint: Queue setup configuration
 * - Qualys: Authentication method (Session vs JWT)
 */
export const populateSourceSpecificSettings = (
  selectedSourceDetails: SourceDetailResponse,
  defaultValuesModel: SourceCreateDynamicFormModel,
  setSelectedSubSourceType: (val: string) => void
): SourceCreateDynamicFormModel => {
  let defaultValues = { ...defaultValuesModel };

  // Mimecast: API version selection
  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.MIMECAST) {
    const mimeCastVersion =
      (selectedSourceDetails?.is_api_v2 ?? true) ? 'Version2' : 'Version1';
    setSelectedSubSourceType(mimeCastVersion);
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: mimeCastVersion
    };
  }

  // Cisco Secure Endpoint: Queue setup configuration
  if (
    Number(selectedSourceDetails.source_type_id) ===
    SOURCE_TYPE.CISCO_SECURE_ENDPOINT
  ) {
    const queueSetup =
      (selectedSourceDetails?.queue_setup_enabled ?? true) ? 'Yes' : 'No';
    setSelectedSubSourceType(queueSetup);
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: queueSetup
    };
  }

  // Qualys: Authentication method
  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.QUALYS) {
    const authMethod =
      selectedSourceDetails?.authentication_method || 'Session';
    setSelectedSubSourceType(authMethod);
    defaultValues = {
      ...defaultValues,
      ['sub_source_type' as string]: authMethod
    };
  }

  return defaultValues;
};

/**
 * Step 10: Configures data provider settings for multi-tenant sources.
 *
 * Loads data provider configurations for:
 * - AWS S3: S3-specific data provider settings
 * - Google Cloud Storage: GCS directory paths
 * - Microsoft: Microsoft-specific data provider settings
 */
export const populateDataProviderSettings = (
  selectedSourceDetails: SourceDetailResponse,
  defaultValuesModel: SourceCreateDynamicFormModel
): SourceCreateDynamicFormModel => {
  let defaultValues = { ...defaultValuesModel };
  // AWS S3
  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.AWS_S3) {
    defaultValues = loadS3DataProvidersFormValues(
      defaultValues,
      selectedSourceDetails
    );
  }

  // Google Cloud Storage
  if (
    Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.GOOGLE_STORAGE
  ) {
    Object.entries(selectedSourceDetails?.data_providers || {}).forEach(
      ([key, val]) => {
        defaultValues = {
          ...defaultValues,
          [`data_provider.${key}.directory_path`]: val?.directory_path,
          [`data_provider.${key}.selected`]: true
        };
      }
    );
  }

  // Microsoft
  if (Number(selectedSourceDetails.source_type_id) === SOURCE_TYPE.MICROSOFT) {
    defaultValues = loadMicrosoftDataProvidersFormValues(
      defaultValues,
      selectedSourceDetails
    );
  }
  return defaultValues;
};
